/**
 * App navbar: title, search, mode switch, notifications, and user menu.
 */

import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { useLayoutStore } from '@/stores/layoutStore';
import { useMode } from '@/contexts/ModeContext';
import { UserMenu } from './UserMenu';
import { NotificationsBell } from './NotificationsBell';
import { ModeSwitchButton } from './ModeSwitchButton';
import { GlobalSearchProvider } from '@/contexts/GlobalSearchContext';
import { GlobalSearchInput } from '@/components/search/GlobalSearchInput';
import { School, Monitor } from 'lucide-react';

/** Top bar: global search, mode switch, notifications, and user menu. */
export const Navbar: React.FC = () => {
  const location = useLocation();
  const { navbarHeight } = useLayoutStore();
  const { canSwitchMode, mode } = useMode();

  /**
   * Hybrid users get a route title; single-mode users see a mode badge instead
   * (the sidebar already conveys the current section).
   */
  const pageTitle = React.useMemo(() => {
    if (!canSwitchMode) {
      return mode === 'remote' ? 'Tutorat distanciel' : 'Tutorat présentiel';
    }

    const titleMap: Record<string, string> = {
      '/': 'Dashboard',
      '/dashboard': 'Dashboard',
      '/post-its': 'Post-its',
      '/calendar': 'Calendrier',
      '/sessions': 'Gestion des Séances',
      '/sessions/mine': 'Mes Séances',
      '/events': 'Gestion des Événements',
      '/tutors': 'Gestion des Tuteurs',
      '/students': 'Gestion des Élèves',
      '/parents': 'Gestion des Parents',
      '/reports': 'Rapports',
      '/admins': 'Gestion des Administrateurs',
      '/establishments': 'Gestion des Établissements',
      '/volunteers': 'Gestion des Bénévoles',
      '/pairs': 'Gestion des Binômes',
      '/leaderboard': 'Classement',
      '/tutorials': 'Tutoriels',
      '/users': 'Utilisateurs',
      '/settings': 'Paramètres',
      '/profile': 'Mon Profil',
      '/components': 'Composants',
    };

    return (
      titleMap[location.pathname] ??
      (mode === 'remote' ? 'Tutorat distanciel' : 'Tutorat présentiel')
    );
  }, [location.pathname, mode, canSwitchMode]);

  return (
    <header
      className="navbar-container"
      style={{ '--navbar-height': `${navbarHeight}px` } as React.CSSProperties}
    >
      <div className="navbar-content">
        <div className="flex items-center gap-4">
          {canSwitchMode ? (
            <ModeSwitchButton />
          ) : (
            <div
              className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold"
              style={{
                border: '1.5px solid color-mix(in srgb, var(--color-1-nuance-3) 60%, transparent)',
                color: 'var(--color-1-nuance-1)',
              }}
            >
              {mode === 'remote' ? (
                <Monitor className="size-4 shrink-0" aria-hidden />
              ) : (
                <School className="size-4 shrink-0" aria-hidden />
              )}
              <span>{pageTitle}</span>
            </div>
          )}
        </div>

        <GlobalSearchProvider>
          <GlobalSearchInput />
        </GlobalSearchProvider>

        <div className="navbar-actions">
          <NotificationsBell />

          <UserMenu />
        </div>
      </div>
    </header>
  );
};
